import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { HomePage } from './HomePage';

import { AuthProvider } from '@/features/auth/AuthContext';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { useAuth } from '@/features/auth/useAuth';
import { VerifyEmailPage } from '@/features/auth/VerifyEmailPage';
import { LandingPage } from '@/features/marketing/LandingPage';

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* Primeiro elemento focável da página, exigido por WCAG 2.4.1. */}
        <a className="skip-link" href="#conteudo">
          Pular para o conteúdo
        </a>

        <Routes>
          {/* Raiz pública: quem chega sem sessão vê a apresentação, não o login. */}
          <Route element={<LandingPage />} path="/" />

          <Route element={<LoginPage />} path="/entrar" />
          <Route element={<RegisterPage />} path="/cadastro" />
          <Route element={<VerifyEmailPage />} path="/verificar-email" />
          <Route element={<ForgotPasswordPage />} path="/esqueci-senha" />
          <Route element={<ResetPasswordPage />} path="/redefinir-senha" />

          <Route
            element={
              <RequireAuth>
                <HomePage />
              </RequireAuth>
            }
            path="/app"
          />

          {/* Rota desconhecida volta para a raiz pública. */}
          <Route element={<Navigate replace to="/" />} path="*" />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

/**
 * Barreira das rotas autenticadas.
 *
 * <p>
 * Enquanto a sessão está sendo restaurada pelo cookie, a tela espera em vez de
 * redirecionar. Sem isso, todo recarregamento piscaria o login antes de voltar
 * — e pareceria, para o usuário, que a sessão tinha caído.
 * </p>
 * <p>
 * Isto é conveniência de navegação, <b>não</b> controle de acesso: quem chamar
 * a API direto continua barrado pelo servidor. Segurança que depende do
 * roteador do cliente não é segurança.
 * </p>
 */
function RequireAuth({ children }: { readonly children: JSX.Element }): JSX.Element {
  const { session, isRestoring } = useAuth();
  const location = useLocation();

  if (isRestoring) {
    return (
      <p aria-live="polite" role="status" style={{ padding: 'var(--space-8)' }}>
        Carregando…
      </p>
    );
  }

  if (session === null) {
    // Guarda de onde a pessoa veio, para voltar ao destino depois de entrar.
    return <Navigate replace state={{ from: location.pathname }} to="/entrar" />;
  }

  return children;
}
